/**
 * Multi-AI Chatbot - Main Application Component
 * 
 * IMPLEMENTED:
 * - Azure AD Authentication with MSAL
 * - Chat interface with message history (in-memory)
 * - Model selector dropdown
 * - Send/receive messages with auth tokens
 * 
 * TO BE IMPLEMENTED:
 * - Session persistence (Phase 2)
 * - Document upload integration (Phase 3)
 * - Token tracking (Phase 3)
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FaPaperPlane, FaSignOutAlt, FaChevronDown, FaLock, FaCopy, FaCheck, FaMoon, FaSun, FaFilePdf, FaFileWord, FaFileImage, FaFileAlt, FaEye, FaSpinner } from 'react-icons/fa';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { tokenRequest } from './authConfig';
import './App.css';
import { API_ENDPOINTS, API_BASE_URL, MODEL_CONFIG } from './api/apiConfig';
import { getConversationMessages, getConversationFeedbacks } from './api/conversationApi';
import { initAuthInterceptor, setupAxiosInterceptor } from './utils/authInterceptor';
import SimpleToast from './components/SimpleToast';
import UploadButton from './components/UploadButton';
import Login from './components/Login';
import DocumentManager from './components/DocumentManager';
import ConversationList from './components/ConversationList';
import FeedbackButtons from './components/FeedbackButtons';
import PdfViewerModal from './components/PdfViewerModal';
import MicButton from './components/MicButton';
import SpeechModal from './components/SpeechModal';
import logo from './P2S_Legence_Logo_White.png';

// Utility: Generate UUID for session IDs
const generateUUID = () => {
  return 'session_' + 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

const normalizeSourceEntry = (source, index = 0) => {
  if (!source) return null;

  if (typeof source === 'string') {
    const trimmed = source.trim();
    if (!trimmed) return null;
    return {
      id: `source-${index}-${trimmed}`,
      title: trimmed,
      subtitle: 'retrieved context',
      filename: trimmed,
      link: null
    };
  }

  const title =
    source.document_title ||
    source.document ||
    source.filename ||
    source.chapter_title ||
    source.source_name ||
    source.title;

  const subtitle =
    source.retrieval_mode ||
    source.source_type ||
    source.reference_type ||
    source.section_number ||
    (source.page ? `page ${source.page}` : null);

  const link = source.source_link || source.document_url || source.blob_url || null;
  const resolvedTitle = title || `Source ${index + 1}`;

  return {
    id: `${resolvedTitle}-${index}`,
    title: resolvedTitle,
    subtitle: subtitle || 'retrieved context',
    filename: source.filename || source.document_title || source.document || resolvedTitle,
    link,
    document_url: source.document_url || source.blob_url || null,
    content_preview: source.content_preview || null
  };
};

const extractSourcesFromMetadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object') {
    return [];
  }

  const fromHr = Array.isArray(metadata.hr_sources)
    ? metadata.hr_sources.map((source, index) =>
        normalizeSourceEntry(
          {
            ...source,
            document_title: source.document,
            source_link: source.source_link,
            retrieval_mode: 'hr_rag'
          },
          index
        )
      )
    : [];

  const fromEngineering = Array.isArray(metadata.eng_sources)
    ? metadata.eng_sources.map((source, index) =>
        normalizeSourceEntry(
          {
            ...source,
            retrieval_mode: 'engineering_rag',
            document_title: source.chapter_title || source.document_title
          },
          index
        )
      )
    : [];

  const fromDocumentsReferenced = Array.isArray(metadata.documents_referenced)
    ? metadata.documents_referenced.map((filename, index) =>
        normalizeSourceEntry(
          {
            filename,
            document_title: filename,
            retrieval_mode: 'attached_document'
          },
          index
        )
      )
    : [];

  return [...fromHr, ...fromEngineering, ...fromDocumentsReferenced].filter(Boolean);
};

const PREVIEWABLE_EXTENSIONS = new Set([
  'pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg',
  'txt', 'md', 'markdown', 'json', 'csv', 'log', 'xml', 'yaml', 'yml',
  'docx', 'xlsx', 'pptx'
]);

const getFileExtensionFromUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  try {
    const parsed = new URL(url);
    const filename = parsed.pathname.split('/').pop() || '';
    const ext = filename.includes('.') ? filename.split('.').pop() : '';
    return ext.toLowerCase();
  } catch {
    const clean = url.split('?')[0];
    const filename = clean.split('/').pop() || '';
    const ext = filename.includes('.') ? filename.split('.').pop() : '';
    return ext.toLowerCase();
  }
};

const isPreviewableFileUrl = (url) => PREVIEWABLE_EXTENSIONS.has(getFileExtensionFromUrl(url));
const THEME_STORAGE_KEY = 'p2s_theme';

// Circular Logo Component (similar to AI-Testing)
const CircularLogo = () => (
  <div className="circular-logo">
    <div className="blue-circle">
      <img src={logo} alt="P2S Logo" className="logo-image" />      
    </div>
  </div>
);

// Model Selector Component — custom dropdown
const ModelSelector = ({ selectedModel, onModelChange, availableModels, isLocked, lockedModel }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const activeModel = availableModels.find(m => m.id === selectedModel) || availableModels[0];

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (modelId) => {
    if (!isLocked) {
      onModelChange(modelId);
      setOpen(false);
    }
  };

  return (
    <div className={`ms-root${isLocked ? ' ms-locked' : ''}`} ref={ref}>
      <button
        className="ms-trigger"
        onClick={() => !isLocked && setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={isLocked ? `Locked to ${MODEL_CONFIG[lockedModel]?.displayName || lockedModel}` : 'Switch AI model'}
      >
        <span className="ms-trigger-label">
          <span className="ms-dot" style={{ background: MODEL_CONFIG[activeModel?.id]?.color || 'rgb(52,106,134)' }} />
          {activeModel?.name || 'Select model'}
        </span>
        {isLocked
          ? <FaLock className="ms-chevron" style={{ fontSize: '10px', opacity: 0.6 }} />
          : <FaChevronDown className={`ms-chevron${open ? ' ms-chevron-open' : ''}`} />
        }
      </button>

      {open && (
        <ul className="ms-dropdown" role="listbox">
          {availableModels.map(model => {
            const isWip = model.status === 'wip';
            return (
              <li
                key={model.id}
                role="option"
                aria-selected={model.id === selectedModel}
                aria-disabled={isWip}
                className={`ms-option${model.id === selectedModel ? ' ms-option-active' : ''}${isWip ? ' ms-option-disabled' : ''}`}
                onClick={() => !isWip && handleSelect(model.id)}
              >
                <span className="ms-dot" style={{ background: MODEL_CONFIG[model.id]?.color || 'rgb(52,106,134)' }} />
                <span className="ms-option-name">{model.name}</span>
                {model.id === selectedModel && <span className="ms-option-check">✓</span>}
                {(isWip || MODEL_CONFIG[model.id]?.tooltip) && (
                  <span className="ms-option-tooltip">{isWip ? 'Work in progress — coming soon' : MODEL_CONFIG[model.id].tooltip}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// Message Component with Markdown support and Feedback
const MessageContent = ({ message, conversationId, accessToken, feedbackCache, onFeedbackSubmit, onViewDocument }) => {
  const isBot = message.role === 'assistant';
  const [copied, setCopied] = React.useState(false);

  const stripMarkdown = (md) => md
    .replace(/```[\s\S]*?```/g, (m) => m.replace(/```(?:\w+)?|```/g, '').trim()) // fenced code blocks
    .replace(/`([^`]+)`/g, '$1')           // inline code
    .replace(/^#{1,6}\s+/gm, '')           // headings
    .replace(/\*\*(.+?)\*\*/g, '$1')       // bold
    .replace(/__(.+?)__/g, '$1')           // bold alt
    .replace(/\*(.+?)\*/g, '$1')           // italic
    .replace(/_(.+?)_/g, '$1')             // italic alt
    .replace(/~~(.+?)~~/g, '$1')           // strikethrough
    .replace(/^\s*[-*+]\s+/gm, '• ')       // unordered list → bullet
    .replace(/^\s*\d+\.\s+/gm, '')         // ordered list numbers
    .replace(/^>\s+/gm, '')                // blockquotes
    .replace(/!\[.*?\]\(.*?\)/g, '')        // images
    .replace(/\[(.+?)\]\(.*?\)/g, '$1')    // links → label only
    .replace(/^[-*_]{3,}$/gm, '')          // horizontal rules
    .replace(/\n{3,}/g, '\n\n')            // collapse excess blank lines
    .trim();

  const handleCopy = () => {
    navigator.clipboard.writeText(stripMarkdown(message.content)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (isBot) {
    return (
      <div className="bot-message-content">
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={{
            // Intercept blob storage links and open in-app preview; all others open in new tab
            a: ({ node, href, children, ...props }) => {
              if (href && href.includes('blob.core.windows.net')) {
                const matchingSource = message.sources?.find(src => {
                  if (!src?.document_url) return false;
                  try {
                    const srcPath = decodeURIComponent(new URL(src.document_url).pathname).toLowerCase();
                    const hrefPath = decodeURIComponent(new URL(href).pathname).toLowerCase();
                    return srcPath === hrefPath;
                  } catch { return false; }
                });
                return (
                  <button
                    className="view-doc-link"
                    onClick={() => onViewDocument && onViewDocument(href, matchingSource?.content_preview)}
                  >
                    {children}
                  </button>
                );
              }
              return (
                <a href={href} {...props} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              );
            }
          }}
        >
          {message.content}
        </ReactMarkdown>
        
        <div className="message-actions">
          {message.show_feedback && message.message_id && conversationId ? (
            <FeedbackButtons
              messageId={message.message_id}
              conversationId={conversationId}
              accessToken={accessToken}
              existingFeedback={feedbackCache[message.message_id] || null}
              onFeedbackSubmit={onFeedbackSubmit}
              prefixSlot={
                <button
                  className={`copy-btn${copied ? ' copy-btn--done' : ''}`}
                  onClick={handleCopy}
                  title={copied ? 'Copied!' : 'Copy to clipboard'}
                >
                  {copied ? <FaCheck /> : <FaCopy />}
                </button>
              }
            />
          ) : (
            <button
              className={`copy-btn${copied ? ' copy-btn--done' : ''}`}
              onClick={handleCopy}
              title={copied ? 'Copied!' : 'Copy to clipboard'}
            >
              {copied ? <FaCheck /> : <FaCopy />}
            </button>
          )}
        </div>
      </div>
    );
  }
  
  return <div className="user-message-content">{message.content}</div>;
};

// Footer Component (similar to AI-Testing)
const Footer = () => {
  return (
    <footer className="footer">
      <div className="footer-left">
        <span className="footer-version">Version 1.2.0</span>
        <span className="footer-divider" aria-hidden="true">|</span>
        <div className="tooltip-container">
          <span className="footer-text" tabIndex={0} role="button" aria-describedby="tooltip-about">About</span>
          <div className="tooltip" id="tooltip-about" role="tooltip">
            AI Assistant<br />
            Developed by the P2S AI Team.
          </div>
        </div>
        <span className="footer-divider" aria-hidden="true">|</span>
        <div className="tooltip-container">
          <span className="footer-text" tabIndex={0} role="button" aria-describedby="tooltip-disclaimer">Disclaimer</span>
          <div className="tooltip" id="tooltip-disclaimer" role="tooltip">
            AI-generated responses may not always be accurate. Verify information before making decisions.
          </div>
        </div>
      </div>
      <div className="footer-right">
        <a href="https://www.p2sinc.com" target="_blank" rel="noopener noreferrer">www.p2sinc.com</a>
        <span className="footer-divider" aria-hidden="true">|</span>
        <span>© {new Date().getFullYear()} P2S All rights reserved.</span>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  // MSAL authentication hooks
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = accounts.length > 0;
  
  // Toast state
  const [toast, setToast] = useState(null);
  
  // State management
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedModel, setSelectedModel] = useState('llama-4-maverick');
  const [availableModels, setAvailableModels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [lockedModel, setLockedModel] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [useDocuments, setUseDocuments] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  
  // Conversation management
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [conversationTitle, setConversationTitle] = useState(null);
  const [conversationMode, setConversationMode] = useState('general'); // 'general', 'hr', or 'engineering'
  const [showSidebar, setShowSidebar] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger to refresh conversation list
  const [showDocumentManager, setShowDocumentManager] = useState(false); // Toggle for document manager
  const [showEngDocumentManager, setShowEngDocumentManager] = useState(false); // Toggle for engineering document manager
  const [showModeSelector, setShowModeSelector] = useState(false); // Mode selector dialog
  const [engUploadedDocs, setEngUploadedDocs] = useState([]); // Engineering service docs (isolated from general uploadedDocs)
  
  // Feedback management
  const [feedbackCache, setFeedbackCache] = useState({}); // Cache of feedbacks by message_id

  // Engineering table modal
  const [tableModal, setTableModal] = useState(null); // holds source object with table_image_url
  const [pdfModal, setPdfModal] = useState(null);    // holds { url: string } for in-app document preview

  // Speech-to-text modal
  const [speechModalOpen, setSpeechModalOpen] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const dragCounter = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'dark' ? 'dark' : 'light';
  });

  // Show toast helper
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  // Initialize auth interceptor on mount
  useEffect(() => {
    initAuthInterceptor(instance, showToast);
    setupAxiosInterceptor();
  }, [instance]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  // Acquire access token when authenticated
  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None) {
      acquireAccessToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, inProgress]);

  // Load available models on startup (only when authenticated)
  useEffect(() => {
    if (isAuthenticated && accessToken) {
      fetchModels();
      
      // Initialize session ID if not set
      if (!sessionId) {
        const initialSessionId = generateUUID();
        setSessionId(initialSessionId);
        console.log(`🆕 Initialized session ID: ${initialSessionId}`);
      }
      
      // Add welcome message
      const userName = accounts[0]?.name || 'User';
      setMessages([
        {
          role: 'assistant',
          content: `Welcome back, ${userName}! Select a model and start chatting.`,
          timestamp: new Date(),
          isWelcome: true
        }
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accessToken]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Auto-resize composer textarea as content grows
  useEffect(() => {
    const textArea = inputRef.current;
    if (!textArea) {
      return;
    }

    textArea.style.height = 'auto';
    textArea.style.height = `${Math.min(textArea.scrollHeight, 210)}px`;
  }, [inputMessage]);

  // Load feedbacks when conversation changes
  useEffect(() => {
    if (currentConversationId && accessToken) {
      loadConversationFeedbacks(currentConversationId);
    } else {
      setFeedbackCache({}); // Clear cache when no conversation
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId, accessToken]);

  // Automatically enable/disable RAG based on uploaded documents
  useEffect(() => {
    console.log(`⚙️ useEffect[uploadedDocs] triggered - count: ${uploadedDocs.length}`);
    if (uploadedDocs.length > 0) {
      console.log(`   Documents:`, uploadedDocs.map(d => d.filename));
    }
    setUseDocuments(uploadedDocs.length > 0);
  }, [uploadedDocs]);

  /**
   * Acquire access token for API calls
   */
  const acquireAccessToken = async () => {
    const request = {
      ...tokenRequest,
      account: accounts[0],
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      setAccessToken(response.accessToken);
      console.log('Access token acquired');
    } catch (error) {
      console.error('Silent token acquisition failed:', error);

      // This runs from a useEffect (no user gesture), so a popup would be
      // blocked by the browser. Use a full-page redirect instead, which
      // browsers never block, to recover from an expired/missing AAD session.
      try {
        showToast('Your session has expired. Redirecting to sign in again...', 'warning');
        await instance.acquireTokenRedirect(request);
      } catch (redirectError) {
        console.error('Token acquisition failed:', redirectError);
        showToast('Unable to refresh your session. Please sign in again.', 'error');
      }
    }
  };

  /**
   * Logout handler
   */
  const handleLogout = () => {
    instance.logoutPopup({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
  };

  const fetchModels = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.MODELS}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      setAvailableModels(response.data.models);
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  /**
   * Load feedbacks for the current conversation
   */
  const loadConversationFeedbacks = async (conversationId) => {
    if (!conversationId || !accessToken) {
      setFeedbackCache({});
      return;
    }
    
    // Clear cache immediately to prevent showing old feedbacks
    setFeedbackCache({});
    console.log(`📥 Loading feedbacks for conversation: ${conversationId}`);
    
    try {
      const response = await getConversationFeedbacks(accessToken, conversationId);
      
      // Build a cache: { message_id: feedback }
      const cache = {};
      if (response && response.feedbacks) {
        response.feedbacks.forEach(feedback => {
          cache[feedback.message_id] = feedback;
        });
      }
      
      setFeedbackCache(cache);
      console.log(`✅ Loaded ${Object.keys(cache).length} feedbacks for conversation ${conversationId}:`, Object.keys(cache));
    } catch (error) {
      console.error('Error loading feedbacks:', error);
      setFeedbackCache({});
    }
  };

  /**
   * Handle feedback submission callback
   */
  const handleFeedbackSubmit = (feedback) => {
    // Only update cache if feedback is for current conversation
    if (feedback.conversation_id === currentConversationId) {
      setFeedbackCache(prev => ({
        ...prev,
        [feedback.message_id]: feedback
      }));
      console.log('✅ Feedback submitted and cached for current conversation:', feedback.message_id);
    } else {
      console.warn('⚠️ Feedback submitted for different conversation, not caching:', feedback.conversation_id, 'vs', currentConversationId);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    // Add user message to chat
    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Log RAG status for debugging
      console.log(`\n${'='.repeat(80)}`);
      console.log('💬 SENDING CHAT MESSAGE');
      console.log(`   Session ID: ${sessionId}`);
      console.log(`   Conversation ID: ${currentConversationId || 'null (new)'}`);
      console.log(`   RAG Enabled: ${useDocuments}`);
      console.log(`   Uploaded Docs: ${uploadedDocs.length}`);
      console.log(`   Model: ${selectedModel}`);
      console.log(`${'='.repeat(80)}\n`);
      
      // Send to backend with conversation ID, auth token, and RAG flag
      const chatBody = {
        message: inputMessage,
        model_name: selectedModel,
        conversation_history: messages
          .filter(m => !m.isWelcome)
          .map(m => ({
            role: m.role,
            content: m.content
          })),
        session_id: sessionId,  // Legacy session ID
        conversation_id: currentConversationId,  // Cosmos DB conversation ID
        conversation_title: conversationTitle,  // Optional title for new conversations
        use_documents: useDocuments,  // Enable RAG with uploaded documents
        save_to_db: true,  // Save to Cosmos DB
        mode: conversationMode  // "general", "hr", or "engineering"
      };

      // Include engineering uploads when in engineering mode and docs are indexed
      if (conversationMode === 'engineering' && engUploadedDocs.length > 0) {
        chatBody.include_uploads = true;
        chatBody.uploaded_by = accounts[0]?.username || accounts[0]?.name || 'anonymous';
        console.log(`   Eng uploads: include_uploads=true, uploaded_by=${chatBody.uploaded_by}, docs=${engUploadedDocs.length}`);
      }

      const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.CHAT}`, chatBody, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'X-Session-Id': sessionId
        }
      });

      // Store model lock on first message
      if (!lockedModel) {
        setLockedModel(response.data.locked_model);
        console.log(`Session locked to model: ${response.data.locked_model}`);
      }
      
      // Store conversation ID from response
      if (response.data.conversation_id) {
        if (!currentConversationId || currentConversationId !== response.data.conversation_id) {
          setCurrentConversationId(response.data.conversation_id);
          console.log(`Conversation ID: ${response.data.conversation_id}`);
          // Trigger conversation list refresh
          setRefreshTrigger(prev => prev + 1);
        }
      }

      // Add AI response to chat
      const responseSources = Array.isArray(response.data.sources) ? response.data.sources : [];
      const activeContextDocs = conversationMode === 'engineering' ? engUploadedDocs : uploadedDocs;
      const fallbackSources = responseSources.length === 0 && useDocuments
        ? activeContextDocs.map(doc => ({
            filename: doc.filename,
            document_title: doc.filename,
            retrieval_mode: 'attached_document'
          }))
        : [];

      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        model: response.data.model_used,
        model_status: response.data.model_status,
        timestamp: new Date(),
        show_feedback: response.data.show_feedback || false,
        message_id: response.data.assistant_message_id,
        metadata: {
          used_rag: useDocuments,
          documents_referenced: activeContextDocs.map(doc => doc.filename)
        },
        sources: [...responseSources, ...fallbackSources]
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Check if it's an auth error
      if (error.response?.status === 401) {
        const errorMessage = {
          role: 'assistant',
          content: 'Your session has expired. Please refresh the page to login again.',
          error: true,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      } else {
        const detail = error.response?.data?.detail ?? '';
        const isTimeout = detail.toLowerCase().includes('timed out') || detail.toLowerCase().includes('timeout');
        const errorMessage = {
          role: 'assistant',
          content: isTimeout
            ? '⏱ The AI model took too long to respond. Please try again — this usually resolves itself.'
            : '❌ Sorry, there was an error processing your request. Please try again.',
          error: true,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      sendMessage();
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const removeAttachedDocument = (documentToRemove, scope = 'general') => {
    if (scope === 'engineering') {
      setEngUploadedDocs(prev => prev.filter(doc => {
        if (documentToRemove.id && doc.id) {
          return doc.id !== documentToRemove.id;
        }
        return doc.filename !== documentToRemove.filename;
      }));
      return;
    }

    setUploadedDocs(prev => prev.filter(doc => {
      if (documentToRemove.id && doc.id) {
        return doc.id !== documentToRemove.id;
      }
      return doc.filename !== documentToRemove.filename;
    }));
  };

  // Toast wrapper for child components
  const handleShowToast = (message, type = 'info') => {
    showToast(message, type);
  };

  const clearChat = () => {
    console.log('🔥🔥🔥 CLEAR CHAT CALLED - CODE VERSION 2.0 🔥🔥🔥');
    console.log('📊 uploadedDocs BEFORE clear:', uploadedDocs.length, uploadedDocs.map(d => d.filename));
    
    setMessages([
      {
        role: 'assistant',
        content: 'Chat cleared! You can now select a different model.',
        timestamp: new Date(),
        isWelcome: true
      }
    ]);
    // Generate new stable session ID for this chat
    const newSessionId = generateUUID();
    setSessionId(newSessionId);
    console.log(`🆕 New chat started with session ID: ${newSessionId}`);
    setLockedModel(null);
    setCurrentConversationId(null);
    setConversationTitle(null);
    setConversationMode('general'); // Reset to general mode
    setFeedbackCache({}); // Clear feedback cache
    setUploadedDocs([]); // Clear documents - RAG automatically disabled via useEffect
    setEngUploadedDocs([]); // Clear engineering uploads
    console.log('📤 Documents cleared - RAG disabled');
    console.log('📊 uploadedDocs AFTER clear: 0');
  };
  
  const handleNewConversation = () => {
    setShowModeSelector(true); // Show mode selector dialog
    console.log('🆕 Opening mode selector for new conversation');
  };
  
  const handleModeSelected = (mode) => {
    console.log(`🆕 Starting new ${mode} conversation`);
    
    // Close modal immediately to prevent double-clicks
    setShowModeSelector(false);
    
    // First: Clear everything including documents
    setUploadedDocs([]); // CRITICAL: Clear documents FIRST
    setEngUploadedDocs([]); // Clear engineering uploads
    setMessages([
      {
        role: 'assistant',
        content: 'Chat cleared! You can now select a different model.',
        timestamp: new Date(),
        isWelcome: true
      }
    ]);
    
    // Generate new stable session ID for this chat
    const newSessionId = generateUUID();
    setSessionId(newSessionId);
    console.log(`🆕 New chat started with session ID: ${newSessionId}`);
    console.log('📤 Documents cleared - RAG disabled');
    
    // Then: Set other states
    setLockedModel(null);
    setCurrentConversationId(null);
    setFeedbackCache({});
    setConversationMode(mode);
    
    // Engineering mode only supports GPT-4.1; reset to default when leaving it
    if (mode === 'engineering') {
      setSelectedModel('gpt-4.1');
    } else {
      setSelectedModel('llama-4-maverick');
    }
    
    // Set conversation title only once, after mode is determined
    const modeLabel = mode === 'hr' ? 'HR Assistant' : mode === 'engineering' ? 'Engineering Assistant' : MODEL_CONFIG[selectedModel]?.displayName || selectedModel;
    setConversationTitle(`Chat with ${modeLabel}`);
  };
  
  const handleSelectConversation = async (conversationId) => {
    try {
      console.log(`Loading conversation: ${conversationId}`);
      
      // Clear feedback cache immediately when switching conversations
      setFeedbackCache({});
      
      // Get conversation messages
      const data = await getConversationMessages(accessToken, conversationId);
      
      // Convert messages to app format
      const loadedMessages = data.messages.map(msg => {
        const metadata = msg.metadata || {};
        const extractedSources = extractSourcesFromMetadata(metadata);

        return {
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.timestamp),
          model: msg.model_used,
          message_id: msg.id,
          metadata,
          sources: extractedSources.length > 0 ? extractedSources : null,
          // For assistant messages from DB, assume show_feedback based on content
          show_feedback: msg.role === 'assistant' && msg.content.length > 50
        };
      });
      
      setMessages(loadedMessages);
      setCurrentConversationId(conversationId);
      setSessionId(conversationId); // Use conversation ID as session ID
      
      // Get conversation details to set model and linked documents
      const conv = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.CONVERSATIONS.GET(conversationId)}`,
        { headers: { 'Authorization': `Bearer ${accessToken}` } }
      );
      setSelectedModel(conv.data.model_name);
      setLockedModel(conv.data.model_name);
      setConversationTitle(conv.data.title);
      setConversationMode(conv.data.mode || 'general'); // Load conversation mode
      
      // Load linked documents if any
      if (conv.data.linked_documents && conv.data.linked_documents.length > 0) {
        console.log(`Conversation has ${conv.data.linked_documents.length} linked document(s)`);
        
        // Fetch full document details
        try {
          const docsResponse = await axios.get(
            `${API_BASE_URL}/api/documents`,
            { headers: { 'Authorization': `Bearer ${accessToken}` } }
          );
          
          // Filter to only include linked documents
          const linkedDocIds = conv.data.linked_documents.map(doc => doc.document_id);
          const linkedDocs = docsResponse.data.documents.filter(doc => 
            linkedDocIds.includes(doc.id)
          );
          
          if (linkedDocs.length > 0) {
            console.log(`Loaded ${linkedDocs.length} linked document(s) for RAG`);
            console.log('📄 Setting uploadedDocs from conversation:', linkedDocs.map(d => d.filename));
            setUploadedDocs(linkedDocs);
            // RAG automatically enabled via useEffect
          }
        } catch (docError) {
          console.error('Failed to load linked documents:', docError);
        }
      } else {
        // No linked documents
        console.log('📄 No linked documents, clearing uploadedDocs');
        setUploadedDocs([]);
      }
      
      console.log(`Loaded conversation with ${loadedMessages.length} messages`);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      alert('Failed to load conversation');
    }
  };
  
  const handleConversationDeleted = () => {
    // Current conversation was deleted, start fresh
    clearChat();
  };

  const handleEngUploadSuccess = (result) => {
    console.log('📤 ENGINEERING DOCUMENT UPLOAD SUCCESS:', result.documents?.map(d => d.filename));
    setEngUploadedDocs(prev => {
      const existingIds = new Set(prev.map(d => d.id));
      const newDocs = (result.documents || []).filter(d => !existingIds.has(d.id));
      return [...prev, ...newDocs];
    });
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `📤 Engineering document uploaded. Processing in background — once ready it will be included in your answers automatically.`,
      timestamp: new Date()
    }]);
  };

  const handleUploadSuccess = (result) => {
    console.log(`\n${'='.repeat(80)}`);
    console.log('📤 DOCUMENT UPLOAD SUCCESS');
    console.log(`   Session ID: ${sessionId}`);
    console.log(`   Documents uploaded: ${result.documents?.length || 0}`);
    if (result.documents && result.documents.length > 0) {
      result.documents.forEach(doc => {
        console.log(`      - ${doc.filename}`);
      });
    }
    console.log(`${'='.repeat(80)}\n`);
    
    const newDocs = result.documents || [];
    console.log('📄 Appending uploadedDocs from upload:', newDocs.map(d => d.filename));
    setUploadedDocs(prev => {
      const existingNames = new Set(prev.map(d => d.filename));
      const merged = [...prev, ...newDocs.filter(d => !existingNames.has(d.filename))];
      return merged;
    });
    // RAG automatically enabled via useEffect when documents present
    
    // Add system message
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `Uploaded ${result.documents?.length || 0} document(s). You can now ask questions about them!`,
      timestamp: new Date()
    }]);
  };

  const handleUploadError = (error) => {
    console.error('Upload failed:', error);
  };

  const uploadFilesFromComposer = async (files) => {
    if (!files.length || !accessToken) return;

    const allowedExt = conversationMode === 'engineering'
      ? ['.pdf', '.docx', '.png', '.jpg', '.jpeg']
      : ['.pdf', '.docx', '.txt', '.png', '.jpg', '.jpeg'];
    const maxSize = 50 * 1024 * 1024;

    for (const file of files) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedExt.includes(ext)) {
        showToast(`File type not supported: ${ext}`, 'error');
        return;
      }
      if (file.size > maxSize) {
        showToast(`File too large: ${file.name}`, 'error');
        return;
      }
    }

    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      showToast(`Uploading ${files.length} file(s)...`, 'info');
      const response = await fetch(`${API_BASE_URL}/api/documents/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}`, 'X-Session-Id': sessionId || '' },
        body: formData
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Upload failed');
      }
      const result = await response.json();
      handleUploadSuccess(result);
    } catch (err) {
      showToast(`Upload failed: ${err.message}`, 'error');
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    await uploadFilesFromComposer(files);
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = [];
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          const ext = file.type.split('/')[1] === 'jpeg' ? 'jpg' : file.type.split('/')[1];
          const named = new File([file], `clipboard-${Date.now()}.${ext}`, { type: file.type });
          imageFiles.push(named);
        }
      }
    }

    if (imageFiles.length > 0) {
      e.preventDefault();
      await uploadFilesFromComposer(imageFiles);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current += 1;
    if (dragCounter.current === 1) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const handleDragOver = (e) => { e.preventDefault(); };

  const handleDocumentsChange = (docs) => {
    // This is called when documents are deleted from Document Manager
    // DO NOT update uploadedDocs here - it pollutes the current chat
    // Documents should only be added via explicit upload or attachment
    console.log('📄 Document Manager list changed, but NOT updating chat uploadedDocs');
  };

  const handleDocumentManagerClick = () => {
    if (conversationMode === 'engineering') {
      setShowEngDocumentManager(prev => !prev);
    } else {
      setShowDocumentManager(prev => !prev);
    }
  };

  const handleDocumentsLinked = async (result) => {
    console.log('Documents linked to conversation:', result);
    
    // If a new conversation was created, switch to it
    if (result.conversationId && result.conversationId !== currentConversationId) {
      console.log(`Switching to new conversation: ${result.conversationId}`);
      await handleSelectConversation(result.conversationId);
      setRefreshTrigger(prev => prev + 1); // Refresh conversation list
      
      // Set the linked documents (RAG automatically enabled via useEffect)
      if (result.documents && result.documents.length > 0) {
        console.log(`Setting ${result.documents.length} linked documents`);
        console.log('📄 Setting uploadedDocs from Document Manager:', result.documents.map(d => d.filename));
        setUploadedDocs(result.documents);
      }
    } else {
      // Existing conversation - merge with current uploadedDocs
      if (result.documents && result.documents.length > 0) {
        console.log(`Adding ${result.documents.length} documents to current chat`);
        setUploadedDocs(prev => {
          // Merge and deduplicate by document ID
          const existingIds = new Set(prev.map(d => d.id));
          const newDocs = result.documents.filter(d => !existingIds.has(d.id));
          const merged = [...prev, ...newDocs];
          console.log('📄 Merged uploadedDocs:', merged.map(d => d.filename));
          return merged;
        });
        setRefreshTrigger(prev => prev + 1); // Refresh conversation list
      }
    }
  };

  const lastAssistantMessage = useMemo(() => {
    return [...messages].reverse().find(message => message.role === 'assistant');
  }, [messages]);

  const activeContextFiles = useMemo(() => {
    return conversationMode === 'engineering' ? engUploadedDocs : uploadedDocs;
  }, [conversationMode, engUploadedDocs, uploadedDocs]);

  const getContextFileUrl = (doc) => {
    if (!doc || typeof doc !== 'object') {
      return null;
    }

    return doc.blob_url || doc.document_url || doc.source_link || doc.url || null;
  };

  const handleOpenContextFile = (doc) => {
    const fileUrl = getContextFileUrl(doc);
    const filename = doc?.filename || 'file';

    if (!fileUrl) {
      showToast(`Unable to open ${filename}. File link is unavailable.`, 'warning');
      return;
    }

    if (isPreviewableFileUrl(fileUrl)) {
      setPdfModal({ url: fileUrl });
      return;
    }

    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  };

  const handlePreviewDocument = (url, highlightText) => {
    if (!url) return;

    if (isPreviewableFileUrl(url)) {
      setPdfModal({ url, highlightText: highlightText || null });
      return;
    }

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const applyPromptFromLastResponse = (instruction) => {
    if (!lastAssistantMessage?.content) {
      showToast('No assistant response available yet.', 'warning');
      return;
    }

    const prompt = `${instruction}\nUse your most recent assistant response in this conversation as the source context. Do not quote it in full unless I ask.`;
    setInputMessage(prompt);
    inputRef.current?.focus();
    showToast('Token-efficient prompt added. Press Enter to send.', 'info');
  };

  const handleResponseAction = async (action) => {
    if (!lastAssistantMessage?.content) {
      showToast('No assistant response available yet.', 'warning');
      return;
    }

    if (action === 'copy') {
      try {
        await navigator.clipboard.writeText(lastAssistantMessage.content);
        showToast('Latest response copied.', 'success');
      } catch {
        showToast('Could not copy response.', 'error');
      }
      return;
    }

    if (action === 'summary') {
      applyPromptFromLastResponse('Summarize your previous response in 5 concise bullets, preserving key facts.');
      return;
    }

    if (action === 'email') {
      applyPromptFromLastResponse('Rewrite your previous response as a polished professional email with a clear subject and next steps.');
      return;
    }

    if (action === 'checklist') {
      applyPromptFromLastResponse('Convert your previous response into a prioritized checklist with owners and due-date placeholders.');
    }
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  // Show loading while acquiring token
  if (!accessToken && inProgress !== InteractionStatus.None) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div className={`app mode-${conversationMode}`}>
      {/* Toast Notification */}
      {toast && (
        <SimpleToast 
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Main Header */}
      <div className="main-header">
        <div className="header-left">
          <CircularLogo />
          <div className="title-stack">
            <p className="welcome-text">Welcome to our</p>
            <h1 className="department-title">AI Assistant</h1>
          </div>
        </div>
        
        <div className="header-controls">
          <ModelSelector 
            selectedModel={lockedModel || selectedModel}
            onModelChange={setSelectedModel}
            availableModels={conversationMode === 'hr' 
              ? availableModels.filter(model => !model.id.includes('claude') && model.id !== 'gpt-4.1')
              : conversationMode === 'engineering'
              ? availableModels.filter(model => model.id === 'gpt-4.1')
              : availableModels.filter(model => model.id !== 'gpt-4.1')
            }
            isLocked={!!lockedModel}
            lockedModel={lockedModel}
          />

          <button
            className="theme-toggle-button"
            onClick={() => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {theme === 'dark' ? <FaSun /> : <FaMoon />}
          </button>
          
          <button 
            className="logout-button"
            onClick={handleLogout}
            title={`Logout (${accounts[0]?.name || 'User'})`}
          >
            <FaSignOutAlt />
          </button>
          </div>
      </div>
      
      {/* Main Content Area */}
      <div className="main-content">
        {/* Mode Selector Modal */}
        {showModeSelector && (
          <div className="modal-overlay" onClick={() => setShowModeSelector(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Select Conversation Mode</h2>
              <div className="mode-options">
                <button 
                  className="mode-option"
                  onClick={() => handleModeSelected('general')}
                >
                  <div className="mode-icon">💼</div>
                  <div className="mode-title">General Mode</div>
                  <div className="mode-description">
                    Marketing & branding assistant with document upload support
                  </div>
                </button>
                <button 
                  className="mode-option"
                  onClick={() => handleModeSelected('hr')}
                >
                  <div className="mode-icon">🏥</div>
                  <div className="mode-title">HR Mode</div>
                  <div className="mode-description">
                    HR policies, benefits, and employee information
                  </div>
                </button>
                <button 
                  className="mode-option"
                  onClick={() => handleModeSelected('engineering')}
                >
                  <div className="mode-icon">⚙️</div>
                  <div className="mode-title">Engineering Mode <span style={{fontSize:'0.7em', color:'#f59e0b', fontWeight:'600'}}>BETA</span></div>
                  <div className="mode-description">
                    California Title 24 code, matrix adoption tables, and building standards
                  </div>
                </button>
              </div>
              <button 
                className="modal-close"
                onClick={() => setShowModeSelector(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        
        {/* Conversation Sidebar */}
        <ConversationList
          accessToken={accessToken}
          currentConversationId={currentConversationId}
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
          onConversationDeleted={handleConversationDeleted}
          refreshTrigger={refreshTrigger}
          onDocumentManagerClick={handleDocumentManagerClick}
          isCollapsed={!showSidebar}
          toggleSidebar={() => setShowSidebar(!showSidebar)}
          documentManagerContent={
            <>
              <DocumentManager 
                accessToken={accessToken}
                sessionId={sessionId}
                conversationId={currentConversationId}
                selectedModel={selectedModel}
                onDocumentsChange={handleDocumentsChange}
                onDocumentsLinked={handleDocumentsLinked}
                isOpen={showDocumentManager}
                onClose={() => setShowDocumentManager(false)}
                showToast={showToast}
              />
              <DocumentManager
                mode="engineering"
                userId={accounts[0]?.username || accounts[0]?.name || 'anonymous'}
                accessToken={accessToken}
                sessionId={sessionId}
                isOpen={showEngDocumentManager}
                onClose={() => setShowEngDocumentManager(false)}
                showToast={showToast}
              />
            </>
          }
        />

        {/* Chat Area */}
        <div className="chat-area">
          {/* Messages Container */}
          <div className="messages-container">
            {messages.map((msg, index) => (
              <div 
                key={index} 
                className={`message-wrapper ${msg.role}`}
              >
                {msg.role === 'assistant' && <CircularLogo />}
                
                <div className={`message ${msg.role} ${msg.error ? 'error' : ''}`}>
                  <MessageContent 
                    message={msg}
                    conversationId={currentConversationId}
                    accessToken={accessToken}
                    feedbackCache={feedbackCache}
                    onFeedbackSubmit={handleFeedbackSubmit}
                    onViewDocument={handlePreviewDocument}
                  />
                  
                  {/* Engineering sources — table image buttons */}
                  {msg.sources && msg.sources.some(s => s.table_image_url) && (
                    <div className="eng-table-links">
                      <span className="eng-table-links-label">Adoption Tables:</span>
                      {msg.sources.filter(s => s.table_image_url).map((src, i) => (
                        <button
                          key={i}
                          className="eng-table-link-btn"
                          onClick={() => setTableModal(src)}
                        >
                          &#128202; {src.chapter_title
                            ? `Part ${src.part_number || '?'} – Ch. ${src.chapter_number || '?'}`
                            : `Table ${i + 1}`}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* User-uploaded doc source badges */}
                  {msg.sources && msg.sources.some(s => s.retrieval_mode === 'user_upload') && (
                    <div className="eng-table-links">
                      <span className="eng-table-links-label">Your Uploads:</span>
                      {msg.sources
                        .filter(s => s.retrieval_mode === 'user_upload')
                        .filter((s, i, arr) => arr.findIndex(x => x.document_title === s.document_title) === i) // dedupe by title
                        .map((src, i) => (
                          <span key={i} className="user-upload-badge">
                            📎 {src.document_title || src.filename || 'Your document'}
                          </span>
                        ))}
                    </div>
                  )}
{/* 
                  {msg.model && (
                    <div className="message-meta">
                      <span className="model-badge" style={{ 
                        backgroundColor: MODEL_CONFIG[msg.model]?.color || '#999' 
                      }}>
                        {MODEL_CONFIG[msg.model]?.icon} {msg.model}
                      </span>
                      {msg.model_status === 'placeholder' && (
                        <span className="placeholder-indicator">Mock Response</span>
                      )}
                    </div>
                  )} */}
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="message-wrapper assistant">
                <CircularLogo />
                <div className="message assistant typing">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="input-area">
            <div
                className={`input-composer${isDragging ? ' drag-over' : ''}`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                {isDragging && (
                  <div className="drop-overlay">
                    <span>Drop files to upload</span>
                  </div>
                )}              {/* Doc indicator — top of card when active */}
              {uploadedDocs.length > 0 && conversationMode === 'general' && (
                <div className="composer-doc-indicator">
                  <span className="composer-doc-icon">📎</span>
                  {uploadedDocs.map((doc, i) => (
                    <button
                      key={doc.id || `${doc.filename}-${i}`}
                      className="composer-doc-chip"
                      title={doc.filename}
                      onClick={() => removeAttachedDocument(doc, 'general')}
                    >
                      <span>{doc.filename}</span>
                      <span className="composer-doc-chip-remove" aria-hidden="true">×</span>
                    </button>
                  ))}
                </div>
              )}
              {engUploadedDocs.length > 0 && conversationMode === 'engineering' && (
                <div className="composer-doc-indicator">
                  <span className="composer-doc-icon">📎</span>
                  {engUploadedDocs.map((doc, i) => (
                    <button
                      key={doc.id || `${doc.filename}-${i}`}
                      className="composer-doc-chip"
                      title={doc.filename}
                      onClick={() => removeAttachedDocument(doc, 'engineering')}
                    >
                      <span>{doc.filename}</span>
                      <span className="composer-doc-chip-remove" aria-hidden="true">×</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Textarea */}
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                onPaste={handlePaste}
                placeholder="Ask anything or drop a file here to upload...(use Ctrl+V to paste images from clipboard)"
                disabled={isLoading}
                rows="2"
                className="message-input"
              />

              {/* Bottom toolbar */}
              <div className="composer-toolbar">
                {conversationMode === 'general' && (
                  <UploadButton
                    disabled={!accessToken || isLoading}
                    accessToken={accessToken}
                    sessionId={sessionId}
                    onUploadSuccess={handleUploadSuccess}
                    onUploadError={handleUploadError}
                    onShowToast={handleShowToast}
                    compact
                  />
                )}
                {conversationMode === 'engineering' && (
                  <UploadButton
                    mode="engineering"
                    userId={accounts[0]?.username || accounts[0]?.name || 'anonymous'}
                    disabled={!accessToken || isLoading}
                    accessToken={accessToken}
                    sessionId={sessionId}
                    onUploadSuccess={handleEngUploadSuccess}
                    onUploadError={handleUploadError}
                    onShowToast={handleShowToast}
                    compact
                  />
                )}
                <MicButton
                  onClick={() => setSpeechModalOpen(true)}
                  disabled={isLoading}
                  title="Voice input"
                  className="composer-action-btn"
                />
                <div className="composer-toolbar-spacer" />
                <button
                  onClick={sendMessage}
                  disabled={isLoading || !inputMessage.trim()}
                  className="send-button"
                  title="Send message (Enter or Ctrl/Cmd+Enter)"
                >
                  <FaPaperPlane />
                </button>
              </div>
            </div>
          </div>
        </div>

        <aside className="right-sidebar" aria-label="Conversation context panel">
          <div className="context-card">
            <h3>Context files</h3>
            {activeContextFiles.length === 0 ? (
              <p className="context-empty">No files attached to this chat yet.</p>
            ) : (
              <ul className="context-file-list">
                {activeContextFiles.map((doc, index) => {
                  const fileUrl = getContextFileUrl(doc);
                  const ext = (doc.filename || '').split('.').pop().toLowerCase();
                  const isProcessing = doc.status === 'processing' || doc.status === 'queued';
                  const FileIcon = ext === 'pdf' ? FaFilePdf
                    : ['docx', 'doc'].includes(ext) ? FaFileWord
                    : ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext) ? FaFileImage
                    : FaFileAlt;
                  return (
                    <li key={doc.id || `${doc.filename}-${index}`}>
                      <button
                        className={`context-file-btn ${fileUrl && !isProcessing ? '' : 'is-disabled'}`}
                        onClick={() => !isProcessing && handleOpenContextFile(doc)}
                        title={isProcessing ? `${doc.filename} — processing…` : fileUrl ? `Preview ${doc.filename}` : `${doc.filename} (link unavailable)`}
                      >
                        <span className="context-file-row">
                          <FileIcon className="context-file-type-icon" />
                          <span className="context-file-name">{doc.filename}</span>
                          {isProcessing
                            ? <FaSpinner className="context-file-action-icon spinning" title="Processing…" />
                            : fileUrl
                            ? <FaEye className="context-file-action-icon" title="Preview" />
                            : null
                          }
                        </span>
                        <span className="source-subtitle">
                          {isProcessing ? 'processing…' : fileUrl ? 'click to preview' : 'link unavailable'}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="context-card">
            <h3>Response tools</h3>
            <div className="sidebar-actions">
              <button className="sidebar-action-btn" onClick={() => handleResponseAction('copy')}>Copy latest response</button>
              <button className="sidebar-action-btn" onClick={() => handleResponseAction('summary')}>Summarize to bullets</button>
              <button className="sidebar-action-btn" onClick={() => handleResponseAction('email')}>Turn into email</button>
              <button className="sidebar-action-btn" onClick={() => handleResponseAction('checklist')}>Create checklist</button>
            </div>
          </div>

        </aside>

      </div>

      {/* PDF Viewer Modal */}
      <PdfViewerModal
        isOpen={!!pdfModal}
        onClose={() => setPdfModal(null)}
        blobUrl={pdfModal?.url}
        highlightText={pdfModal?.highlightText}
        accessToken={accessToken}
      />

      {/* Speech-to-Text Modal */}
      <SpeechModal
        isOpen={speechModalOpen}
        onClose={() => setSpeechModalOpen(false)}
        onConfirm={(text) => {
          setInputMessage((prev) => prev ? `${prev} ${text}` : text);
          setSpeechModalOpen(false);
        }}
        fieldLabel="Message"
        accessToken={accessToken}
      />

      {/* Engineering Table Image Modal */}
      {tableModal && (
        <div className="modal-overlay" onClick={() => setTableModal(null)}>
          <div className="eng-table-modal" onClick={e => e.stopPropagation()}>
            <div className="eng-table-modal-header">
              <div>
                <div className="eng-table-modal-title">
                  Matrix Adoption Table
                  {tableModal.part_number && ` — Part ${tableModal.part_number}`}
                  {tableModal.chapter_number && `, Ch. ${tableModal.chapter_number}`}
                </div>
                {tableModal.chapter_title && (
                  <div className="eng-table-modal-subtitle">{tableModal.chapter_title}</div>
                )}
              </div>
              <button className="eng-table-modal-close" onClick={() => setTableModal(null)}>&#x2715;</button>
            </div>
            <div className="eng-table-modal-body">
              <img src={tableModal.table_image_url} alt="Matrix Adoption Table" className="eng-table-modal-img" />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default App;
